/** Where a town can stand, and which town.
 *
 * The site search (`findTownSite`), the river erasure a chosen site needs
 * (`drySite`), the doorstep and apron helpers, and the faction-to-def lookups.
 * These are used both by the layout pass in `generate.ts` and by the
 * neutral-town pass in `neutralTowns.ts`, so they live below both.
 */

import type { AdventureMapState, ContentRegistry, FactionId } from "@game/core";
import { setRiver } from "@game/core";
import type { Zone } from "./biomes.js";
import type { Grid } from "./grid.js";
import { allNeighbours, chebyshev, idx, inside, walkable, xOf, yOf } from "./grid.js";
import type { Rng } from "./rng.js";
import { int } from "./rng.js";

/**
 * Every faction the map can actually draw a town for. A faction whose
 * `town_<id>` def is missing is skipped rather than substituted, because a
 * Saltreach that renders as a Castle is a lie the player cannot see through.
 */
export function townFactions(content: ContentRegistry): FactionId[] {
  return [...content.factions.keys()]
    .filter((f) => content.mapObjects.has(`town_${f as string}`))
    .sort();
}

export const townDefFor = (content: ContentRegistry, f: FactionId): string =>
  content.mapObjects.has(`town_${f as string}`) ? `town_${f as string}` : "town_sunspire";

/**
 * Erase every river that runs under a town site, and return the rest.
 *
 * The whole channel goes, not just the cells under the walls: a channel that
 * stops at a town wall is a river running out of the gate, which is exactly
 * the picture this exists to prevent.
 */
export function drySite(m: AdventureMapState, g: Grid, site: TownSite, rivers: number[][]): number[][] {
  const under = new Set<number>();
  for (let dy = -3; dy <= 0; dy++) {
    for (let dx = 0; dx <= 5; dx++) {
      if (inside(g, site.x + dx, site.y + dy)) under.add(idx(g, site.x + dx, site.y + dy));
    }
  }
  return rivers.filter((river) => {
    if (!river.some((i) => under.has(i))) return true;
    for (const i of river) setRiver(m, xOf(g, i), yOf(g, i), 0, "none");
    return false;
  });
}

export interface TownSite {
  /** Footprint anchor: the bottom-left cell of the six-by-four block. */
  readonly x: number;
  readonly y: number;
  /** Entrance cell, bottom-centre of the footprint. */
  readonly entX: number;
  readonly entY: number;
}

/**
 * A town needs six by four of clear, dry, level ground and a doorstep in front
 * of it. The search walks out from the zone seed, so a town lands in the middle
 * of its own territory rather than in whichever corner happened to be free.
 */
export function findTownSite(
  m: AdventureMapState, r: Rng, zone: Zone, zoneOf: Int32Array,
  avoid: ReadonlySet<number> = new Set(),
): TownSite | null {
  const g: Grid = { width: m.width, height: m.height };
  const ranked = zone.cells.slice()
    .filter((i) => walkable(m, i) && !avoid.has(i))
    .sort((a, b) => chebyshev(g, a, zone.seed) - chebyshev(g, b, zone.seed) || a - b)
    .slice(0, 600);
  jitterOrder(r, ranked, 24);

  // Own zone first, then anywhere, then anywhere even across a river - the
  // caller erases a river a site had to be found on.
  for (const [ownZoneOnly, allowRiver] of [[true, false], [false, false], [false, true]] as const) {
    for (const cell of ranked) {
      const ax = xOf(g, cell) - 3, ay = yOf(g, cell);
      let ok = true;
      for (let dy = -3; dy <= 0 && ok; dy++) {
        for (let dx = 0; dx <= 5 && ok; dx++) {
          const cx = ax + dx, cy = ay + dy;
          if (!inside(g, cx, cy)) { ok = false; break; }
          const i = idx(g, cx, cy);
          // A river under the footprint would be drawn running out of the
          // town gate; `tryPlaceAt` refuses it, so the site search must too.
          if (!walkable(m, i) || m.occupant[i] !== 0 || avoid.has(i)
            || (m.river[i] !== 0 && !allowRiver)) ok = false;
          else if (ownZoneOnly && zoneOf[i] !== zone.id) ok = false;
        }
      }
      if (!ok) continue;
      const entX = ax + 3, entY = ay;
      let apron = 0;
      for (let dx = -1; dx <= 1; dx++) {
        if (!inside(g, entX + dx, entY + 1)) continue;
        if (walkable(m, idx(g, entX + dx, entY + 1))) apron++;
      }
      if (apron < 2) continue;
      return { x: ax, y: ay, entX, entY };
    }
  }
  return null;
}

/** Shuffle inside a sliding window: keeps the distance ordering, breaks ties. */
function jitterOrder(r: Rng, xs: number[], window: number): void {
  for (let i = 0; i + 1 < xs.length; i += window) {
    const end = Math.min(xs.length, i + window);
    for (let j = end - 1; j > i; j--) {
      const k = i + int(r, j - i + 1);
      const a = xs[j]!, b = xs[k]!;
      xs[j] = b; xs[k] = a;
    }
  }
}

export function heroCell(m: AdventureMapState, g: Grid, entX: number, entY: number): [number, number] {
  const buf: number[] = [];
  for (const nb of allNeighbours(g, idx(g, entX, entY), buf)) {
    if (walkable(m, nb) && m.occupant[nb] === 0) return [xOf(g, nb), yOf(g, nb)];
  }
  return [entX, entY];
}

export function reserveAround(reserved: Uint8Array, g: Grid, x: number, y: number, radius: number): void {
  for (let dy = -radius; dy <= radius; dy++) {
    for (let dx = -radius; dx <= radius; dx++) {
      if (inside(g, x + dx, y + dy)) reserved[idx(g, x + dx, y + dy)] = 1;
    }
  }
}
